import { inject, Injectable } from '@angular/core';
import {
  collection,
  collectionSnapshots,
  doc,
  Firestore,
} from '@angular/fire/firestore';
import {
  AnalysisResultsVariantStrideListItem,
  StrideListItem,
  StrideListItemDetail,
  Strides,
  StridesStoreItem,
} from '@sleipai/sleip-models';
import { combineLatest, from, map, Observable, take } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';

@Injectable({
  providedIn: 'root',
})
export class AnalysisStridesService {
  constructor() {}

  private firestore = inject(Firestore);
  private functions = inject(Functions);

  loadSharedAnalysisResultsStridesByIdAndKey(
    publicRecordingId: string,
    publicKey: string,
    version: string = 'v2',
    recordingId: string = ''
  ): Observable<StridesStoreItem> {
    const sharedAnalysisResultsStrideListCallable = httpsCallable(
      this.functions,
      'publicSharedRecordingsApi-getStrides'
    );

    const paramData = {
      publicRecordingId,
      key: publicKey,
      version,
      recordingId,
    };

    const strideList$ = from(
      sharedAnalysisResultsStrideListCallable(paramData)
    ).pipe(
      map((data) => data.data as Strides)
    );

    const strideStoreItem$: Observable<StridesStoreItem> = strideList$.pipe(
      map((strides) => {
        return {
          strides: strides,
          id: recordingId || publicRecordingId,
        };
      })
    );

    return strideStoreItem$;
  }

  loadAnalysisResultsStridesById(
    recordingId: string,
    isFlexion: boolean
  ): Observable<StridesStoreItem> {
    const analysisResultRef = doc(
      this.firestore,
      `analysisResults/${recordingId}`
    );
    const stridesSubCollectionRef = collection(analysisResultRef, 'strides');
    const stridesSubCollectionData$ = collectionSnapshots(
      stridesSubCollectionRef
    );

    if (isFlexion) {
      const flexionStridesSubCollectionRef = collection(
        this.firestore,
        `analysisResults/${recordingId}/variantAnalysisResults/flexion/strides`
      );
      const flexionStridesSubCollectionData$ = collectionSnapshots(
        flexionStridesSubCollectionRef
      );

      return combineLatest([
        stridesSubCollectionData$,
        flexionStridesSubCollectionData$,
      ]).pipe(
        take(1),
        map(([stridesSnap, flexionStridesSnap]) => {
          const originalStrides: Record<string, StrideListItemDetail> =
            stridesSnap.reduce(
              (results: Record<string, StrideListItemDetail>, u) => (
                (results[u.id] = u.data() as StrideListItemDetail), results
              ),
              {}
            );

          const flexionStrides: Record<string, StrideListItemDetail> =
            flexionStridesSnap.reduce(
              (results: Record<string, StrideListItemDetail>, u) => (
                (results[u.id] = u.data() as StrideListItemDetail), results
              ),
              {}
            );

          const allBodyPartIds = Array.from(
            new Set([
              ...Object.keys(originalStrides),
              ...Object.keys(flexionStrides),
            ])
          );

          const mergedStrides: Record<string, StrideListItemDetail> = {};

          allBodyPartIds.forEach((bodyPartId) => {
            const originalBodyPartDoc = originalStrides[bodyPartId];
            const flexionBodyPartDoc = flexionStrides[bodyPartId];

            if (flexionBodyPartDoc) {
              const originalStrideList: StrideListItem[] =
                originalBodyPartDoc?.strideList || [];
              const flexionStrideList: AnalysisResultsVariantStrideListItem[] =
                (flexionBodyPartDoc.strideList as unknown as AnalysisResultsVariantStrideListItem[]) ||
                [];

              const mergedStrideList: StrideListItem[] = flexionStrideList.map(
                (
                  flexionStride: AnalysisResultsVariantStrideListItem,
                  idx: number
                ) => {
                  const originalStride = originalStrideList[idx];
                  return originalStride
                    ? { ...originalStride, ...flexionStride }
                    : (flexionStride as unknown as StrideListItem);
                }
              );

              mergedStrides[bodyPartId] = {
                ...originalBodyPartDoc,
                ...flexionBodyPartDoc,
                strideList: mergedStrideList,
              };
            } else if (originalBodyPartDoc) {
              mergedStrides[bodyPartId] = { ...originalBodyPartDoc };
            }
          });

          return {
            strides: mergedStrides as unknown as Strides,
            id: recordingId,
          };
        })
      );
    } else {
      return stridesSubCollectionData$.pipe(
        take(1),
        map((stridesSnap) => {
          const strides = stridesSnap.reduce(
            (results: any, u) => ((results[u.id] = u.data()), results),
            {}
          );
          return {
            strides: strides as Strides,
            id: recordingId,
          };
        })
      );
    }
  }
}
